import { nestedListProps } from "./TermsPanel";
import React, { useMemo } from "react";
import { Autocomplete, Box, IconButton, InputAdornment, TextField, Typography, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteIcon from "@mui/icons-material/Delete";
import Defaults from "../../../helper/defaults";
import RangeFilterControls from "./RangeFilterControls";

import ChipSearchDropdown from "./ChipSearchDropdown";
import FilterPanelActions from "./FilterPanelActions";
import { EMPLOYEE_SCALE, REVENUE_SCALE } from "../../../helper/rangeFilter";
import { fieldSx, panelLabelSx } from "./filterPanelStyles";
import useDebouncedFilterSelection from "./useDebouncedFilterSelection";

function toIndustryLabel(option) {
  if (typeof option === "string") return option;
  return option?.description || option?.code || "";
}

function normalizeIndustryValues(value) {
  return (value || [])
    .map((item) => (typeof item === "string" ? item : toIndustryLabel(item)))
    .filter(Boolean);
}

function IndustryMultiSelect({
  id, options, value, onChange, placeholder, disabled, autoFocus = false, onApply, onClear,
}) {
  return <ChipSearchDropdown id={id} options={options}
    value={Array.isArray(value) ? value : []}
    onChange={(next) => onChange(normalizeIndustryValues(next))}
    placeholder={placeholder} disabled={disabled} autoFocus={autoFocus}
    getOptionLabel={toIndustryLabel} emptyLabel="No industries match"
    onApply={onApply} onClear={onClear} />;
}

export { TermsPanel } from "./TermsPanel";

export function OperatingModelPanel({
  industryChoices,
  industryInclude,
  setIndustryInclude,
  industryExclude,
  setIndustryExclude,
  disabled,
  onApply,
  debounceMultiSelectUpdates = true,
}) {
  const theme = useTheme();
  const hasValues = Boolean(industryInclude?.length || industryExclude?.length);
  const excludeLabel = "Exclude companies in these exact industries";
  const controlledSelection = useMemo(
    () => [
      Array.isArray(industryInclude) ? industryInclude : [],
      Array.isArray(industryExclude) ? industryExclude : [],
    ],
    [industryExclude, industryInclude]
  );
  const industrySelection = useDebouncedFilterSelection(
    controlledSelection,
    ([nextInclude = [], nextExclude = []]) => {
      setIndustryInclude(nextInclude);
      setIndustryExclude(nextExclude);
    }
  );
  const [includeDraft = [], excludeDraft = []] = debounceMultiSelectUpdates
    ? industrySelection.draft
    : controlledSelection;
  const applyPanel = () => {
    if (debounceMultiSelectUpdates) industrySelection.flush();
    onApply?.();
  };

  const options = useMemo(
    () =>
      (industryChoices || [])
        .map(toIndustryLabel)
        .filter(Boolean)
        .filter((label, index, all) => all.indexOf(label) === index),
    [industryChoices]
  );

  return (
    <Box sx={{ opacity: disabled ? 0.45 : 1, pointerEvents: disabled ? "none" : "auto" }}>
      <Typography
        component="p"
        sx={{
          ...panelLabelSx(theme),
          mb: 0.5,
          display: "flex",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 0.5,
        }}
      >
        Include these industries
      </Typography>
      <IndustryMultiSelect
        id="v2-industry-include-autocomplete"
        options={options}
        value={includeDraft}
        onChange={(nextInclude) =>
          debounceMultiSelectUpdates
            ? industrySelection.schedule([nextInclude, excludeDraft])
            : setIndustryInclude(nextInclude)
        }
        placeholder="Search industries to include..."
        disabled={disabled}
        onApply={applyPanel}
        onClear={() => debounceMultiSelectUpdates
          ? industrySelection.commitNow([[], excludeDraft])
          : setIndustryInclude([])}
      />
      <Typography
        component="p"
        sx={{
          ...panelLabelSx(theme),
          mt: 1.25,
          mb: 0.5,
          display: "flex",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 0.5,
        }}
      >
        {excludeLabel}
      </Typography>
      <IndustryMultiSelect
        id="v2-industry-exclude-autocomplete"
        options={options}
        value={excludeDraft}
        onChange={(nextExclude) =>
          debounceMultiSelectUpdates
            ? industrySelection.schedule([includeDraft, nextExclude])
            : setIndustryExclude(nextExclude)
        }
        placeholder="Search industries to exclude..."
        disabled={disabled}
        onApply={applyPanel}
        onClear={() => debounceMultiSelectUpdates
          ? industrySelection.commitNow([includeDraft, []])
          : setIndustryExclude([])}
      />
      <FilterPanelActions
        clearDisabled={!hasValues || disabled}
        onClear={() => {
          if (debounceMultiSelectUpdates) industrySelection.commitNow([[], []]);
          else { setIndustryInclude([]); setIndustryExclude([]); }
        }}
        onApply={applyPanel}
      />
    </Box>
  );
}

export function LocationPanel({
  locations,
  setLocations,
  locationChoices,
  disabled,
  onApply,
  debounceMultiSelectUpdates = true,
}) {
  const theme = useTheme();
  const controlledLocations = Array.isArray(locations) ? locations : [];
  const locationSelection = useDebouncedFilterSelection(locations, setLocations);
  const draftLocations = debounceMultiSelectUpdates
    ? locationSelection.draft
    : controlledLocations;

  const hasDraftValues = draftLocations.length > 0;

  return (
    <Box sx={{ opacity: disabled ? 0.45 : 1, pointerEvents: disabled ? "none" : "auto" }}>
      <Typography
        component="p"
        sx={{
          ...panelLabelSx(theme),
          mb: 0.5,
          display: "flex",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 0.5,
        }}
      >
        Headquarters
      </Typography>
      <ChipSearchDropdown
        id="v4-location"
        data-testid="v4-location-chip-dropdown"
        options={locationChoices || []}
        value={draftLocations}
        onChange={debounceMultiSelectUpdates ? locationSelection.schedule : setLocations}
        placeholder="Search locations..."
        disabled={disabled}
        emptyLabel="No locations match"
        onApply={() => {
          if (debounceMultiSelectUpdates) locationSelection.flush();
          onApply?.();
        }}
        onClear={() => debounceMultiSelectUpdates
          ? locationSelection.commitNow([])
          : setLocations([])}
      />
      <FilterPanelActions
        clearDisabled={!hasDraftValues || disabled}
        onClear={() => debounceMultiSelectUpdates
          ? locationSelection.commitNow([])
          : setLocations([])}
        onApply={() => {
          if (debounceMultiSelectUpdates) locationSelection.flush();
          onApply?.();
        }}
      />
    </Box>
  );
}

export function RevenuePanel(props) {
  return (
    <RangeFilterControls
      title="Revenue"
      scale={REVENUE_SCALE}
      lowerBound={props.revenueLowerBound}
      upperBound={props.revenueUpperBound}
      setLowerBound={props.setRevenueLowerBound}
      setUpperBound={props.setRevenueUpperBound}
      disabled={props.disabled}
      onApply={props.onApply}
      onClear={() => {
        props.setRevenueLowerBound(Defaults.search.revenueLowerBound);
        props.setRevenueUpperBound(Defaults.search.revenueUpperBound);
      }}
    />
  );
}

export function EmployeePanel(props) {
  return (
    <RangeFilterControls
      title="Employee"
      scale={EMPLOYEE_SCALE}
      lowerBound={props.employeesLowerBound}
      upperBound={props.employeesUpperBound}
      setLowerBound={props.setEmployeesLowerBound}
      setUpperBound={props.setEmployeesUpperBound}
      disabled={props.disabled}
      onApply={props.onApply}
      onClear={() => {
        props.setEmployeesLowerBound(Defaults.search.employeesLowerBound);
        props.setEmployeesUpperBound(Defaults.search.employeesUpperBound);
      }}
    />
  );
}

export function SavedSearchesPanel({
  savedSearchChoices,
  selectedSavedSearch,
  onSelectSavedSearch,
  onDeleteSavedSearch,
}) {
  const theme = useTheme();

  return (
    <Box>
      <Typography component="p" sx={{ ...panelLabelSx(theme), mb: 0.5 }}>
        Search Name
      </Typography>
      <Autocomplete
        options={savedSearchChoices}
        id="v2-saved-searches-autocomplete"
        getOptionLabel={(option) => option.name || ""}
        value={selectedSavedSearch}
        onChange={onSelectSavedSearch}
        {...nestedListProps}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            variant="outlined"
            placeholder="Search saved searches..."
            sx={fieldSx(theme)}
            autoFocus
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...listItemProps } = props;
          return (
            <li
              key={key}
              {...listItemProps}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
                borderRadius: 8,
                marginBottom: 0,
                minHeight: 34,
                paddingTop: 6,
                paddingBottom: 6,
              }}
            >
              <Typography variant="body2" noWrap sx={{ flexGrow: 1, marginRight: 2, fontWeight: 550, fontSize: 13 }}>
                {option.name}
              </Typography>
              <IconButton
                edge="end"
                aria-label="delete"
                size="small"
                onClick={(e) => onDeleteSavedSearch(e, option)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                sx={{
                  color: theme.palette.action.disabled,
                  "&:hover": {
                    color: theme.palette.error.main,
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </li>
          );
        }}
      />
    </Box>
  );
}
